import React, { useState, useRef, useEffect } from 'react';
import Container from '@mui/material/Container';
import Grid from '@mui/material/Grid';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import Paper from '@mui/material/Paper';
import Button from '@mui/material/Button';
import TextField from '@mui/material/TextField';
import InputAdornment from '@mui/material/InputAdornment';
import Divider from '@mui/material/Divider';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import IconButton from '@mui/material/IconButton';
import AddIcon from '@mui/icons-material/Add';
import DeleteIcon from '@mui/icons-material/Delete';
import EqualizerIcon from '@mui/icons-material/InsertChartOutlined';
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  Tooltip,
  Legend,
  ResponsiveContainer
} from 'recharts';
import Tabs from '@mui/material/Tabs';
import Tab from '@mui/material/Tab';

import { AutoSaveProvider, useAutoSaveState, SaveButton, NameField } from '../lib/useAutoSaveState'
import toFixed from '../lib/toFixed'
import SixDofSim from '../lib/6dof'

// Unit conversion functions
const grainsToKg = (grains) => grains * 0.00006479891;
const gcm2ToKgm2 = (gcm2) => gcm2 * 1e-7;
const inchesToMeters = (inches) => inches * 0.0254;
const fpsToMps = (fps) => fps * 0.3048;
const mpsToFps = (mps) => mps * 3.28084;
const inhgToPa = (inhg) => inhg * 3386.39;
const fToK = (f) => (f - 32) * 5/9 + 273.15;
const mphToMps = (mph) => mph * 0.44704;
const metersToYards = (m) => m * 1.09361;
const metersToInches = (m) => m * 39.3701;
const degToRad = deg => deg * (Math.PI / 180)

const CoefficientTable = ({ name, data, onChange }) => {
  const handleAddRow = () => {
    onChange([...data, [0, 0]])
  }

  const handleDeleteRow = (index) => {
    onChange(data.filter((_, i) => i !== index))
  }

  const handleChange = (index, field, value) => {
    const newData = [...data]
    newData[index][field] = parseFloat(value) || 0
    onChange(newData)
  }

  const handleBlur = () => {
    const newData = [...data]
    newData.sort((a, b) => a[0] - b[0])
    onChange(newData)
  }

  return (
    <TableContainer component={Paper} sx={{ mb: 2 }}>
      <Typography variant="h6" sx={{ p: 2 }}>{name}</Typography>
      <Box sx={{ p: 2 }}>
        <ResponsiveContainer width="100%" height={200}>
          <LineChart data={data.map(([mach, value]) => ({ mach, value }))}>
            <XAxis dataKey="mach" label={{ value: 'Mach', position: 'insideBottom', offset: -5 }} />
            <YAxis label={{ value: 'Value', angle: -90, position: 'insideLeft' }} />
            <Tooltip />
            <Line type="monotone" dataKey="value" stroke="#8884d8" dot={true} />
          </LineChart>
        </ResponsiveContainer>
      </Box>
      <Table size="small">
        <TableHead>
          <TableRow>
            <TableCell>Mach</TableCell>
            <TableCell>Value</TableCell>
            <TableCell align="right">
              <IconButton size="small" onClick={handleAddRow}>
                <AddIcon />
              </IconButton>
            </TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {data.map((row, index) => (
            <TableRow key={index}>
              <TableCell>
                <TextField
                  size="small"
                  type="number"
                  value={row[0]}
                  onChange={e => handleChange(index, 0, e.target.value)}
                  onBlur={handleBlur}
                />
              </TableCell>
              <TableCell>
                <TextField
                  size="small"
                  type="number"
                  value={row[1]}
                  onChange={e => handleChange(index, 1, e.target.value)}
                  onBlur={handleBlur}
                />
              </TableCell>
              <TableCell align="right">
                <IconButton size="small" onClick={() => handleDeleteRow(index)}>
                  <DeleteIcon />
                </IconButton>
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </TableContainer>
  )
}

const SixDof = () => {
  // Bullet Properties
  const [mass, setMass] = useAutoSaveState('mass', '168') // grains
  const [Ix, setIx] = useAutoSaveState('Ix', '.7151') // g.cm^2
  const [Iy, setIy] = useAutoSaveState('Iy', '5.197') // g.cm^2
  const [diameter, setDiameter] = useAutoSaveState('diameter', '0.308') // inches
  const [twistRate, setTwistRate] = useAutoSaveState('twistRate', '12') // inches per turn

  // Initial Conditions
  const [v0, setV0] = useAutoSaveState('v0', '2600') // fps
  const [phi0, setPhi0] = useAutoSaveState('phi0', '0.0006')
  const [theta0, setTheta0] = useAutoSaveState('theta0', '0')
  const [pitchRate0, setPitchRate0] = useAutoSaveState('pitchRate0', '25')
  const [yawRate0, setYawRate0] = useAutoSaveState('yawRate0', '0')
  const [alpha0, setAlpha0] = useAutoSaveState('alpha0', '0')
  const [beta0, setBeta0] = useAutoSaveState('beta0', '0')

  // Environment
  const [airPressure, setAirPressure] = useAutoSaveState('airPressure', '29.92') // inHg
  const [temperature, setTemperature] = useAutoSaveState('temperature', '59') // F
  const [windSpeed, setWindSpeed] = useAutoSaveState('windSpeed', '10') // mph
  const [windDirection, setWindDirection] = useAutoSaveState('windDirection', '90')

  // Control Parameters
  const [maxTime, setMaxTime] = useAutoSaveState('maxTime', '0.12')
  const [dt, setDt] = useAutoSaveState('dt', '0.00001')
  const [resultInterval, setResultInterval] = useAutoSaveState('resultInterval', '0.0001')

  const [result, setResult] = useState('')
  const canvasRef = useRef(null)
  const canvasRef2 = useRef(null)
  const [formTab, setFormTab] = useState(0);
  const [coeffTab, setCoeffTab] = useState(0);

  // Coefficient Tables
  const [Cd0, setCd0] = useAutoSaveState('Cd0', [[0.00, 0.140], [0.80, 0.140], [0.85, 0.142], [0.90, 0.160], [0.95, 0.240], [1.00, 0.430], [1.05, 0.449], [1.10, 0.447], [1.20, 0.434], [1.40, 0.410], [1.60, 0.385], [1.80, 0.365], [2.00, 0.350], [2.20, 0.339], [2.50, 0.320]])
  const [Cda2, setCda2] = useAutoSaveState('Cda2', [[0.00, 2.9], [0.95, 2.9], [1.00, 3.0], [1.05, 3.1], [1.10, 3.6], [1.20, 6.5], [1.40, 7.6], [1.60, 7.3], [1.80, 6.8], [2.00, 6.1], [2.20, 5.4], [2.50, 4.4]])
  const [Cla, setCla] = useAutoSaveState('Cla', [[0.00, 1.75], [0.50, 1.63], [0.80, 1.45], [0.85, 1.40], [0.90, 1.35], [0.95, 1.30], [1.00, 1.35], [1.05, 1.55], [1.10, 1.70], [1.20, 1.90], [1.40, 2.15], [1.60, 2.32], [1.80, 2.45], [2.00, 2.58], [2.20, 2.68], [2.50, 2.85]])
  const [Cla2, setCla2] = useAutoSaveState('Cla2', [[0.00, 0.0], [0.95, 0.0], [1.00, 0.0], [1.05, 0.0], [1.10, 0.0], [1.20, 0.0], [1.40, 0.0], [1.60, 0.0], [1.80, 0.0], [2.00, 0.0], [2.20, 0.0], [2.50, 0.0]])
  const [Clp, setClp] = useAutoSaveState('Clp', [[0.00, -0.0150], [0.50, -0.0125], [0.80, -0.0108], [0.85, -0.0107], [0.90, -0.0105], [0.95, -0.0103], [1.00, -0.0100], [1.05, -0.0099], [1.10, -0.0098], [1.20, -0.0095], [1.40, -0.0088], [1.60, -0.0083], [1.80, -0.0080], [2.00, -0.0075], [2.20, -0.0073], [2.50, -0.0068]])
  const [Cma0, setCma0] = useAutoSaveState('Cma0', [[0.00, 3.05], [0.50, 3.26], [0.80, 3.38], [0.85, 3.40], [0.90, 3.43], [0.95, 3.45], [1.00, 3.24], [1.05, 3.17], [1.10, 3.15], [1.20, 3.12], [1.40, 3.06], [1.60, 2.98], [1.80, 2.88], [2.00, 2.79], [2.20, 2.69], [2.50, 2.56]])
  const [Cma2, setCma2] = useAutoSaveState('Cma2', [[0.00, -4.30], [0.95, -4.30], [1.00, -4.35], [1.05, -4.40], [2.50, -4.40]])
  const [CmqCma0, setCmqCma0] = useAutoSaveState('CmqCma0', [[0.00, 1.20], [1.05, 1.20], [1.10, 0.50], [1.20, -3.6], [1.40, -7.3], [1.60, -8.2], [2.50, -8.2]])
  const [CmqCma2, setCmqCma2] = useAutoSaveState('CmqCma2', [[0.00, 0.0], [0.95, 0.0], [1.00, 0.0], [1.05, 0.0], [1.10, 0.0], [1.20, 0.0], [1.40, 0.0], [1.60, 0.0], [1.80, 0.0], [2.00, 0.0], [2.20, 0.0], [2.50, 0.0]])
  const [Cmpa0, setCmpa0] = useAutoSaveState('Cmpa0', [[0.00, -2.60], [0.90, -2.60], [1.10, -1.35], [1.40, -0.51], [1.70, -0.33], [2.5, -0.33]])
  const [Cmpa2, setCmpa2] = useAutoSaveState('Cmpa2', [[0.00, 50], [0.90, 50], [1.10, 50], [1.40, 50], [1.70, 50], [2.5, 50]])
  const [Cypa, setCypa] = useAutoSaveState('Cypa', [[0.00, 0.0], [0.95, 0.0], [1.00, 0.0], [1.05, 0.0], [1.10, 0.0], [1.20, 0.0], [1.40, 0.0], [1.60, 0.0], [1.80, 0.0], [2.00, 0.0], [2.20, 0.0], [2.50, 0.0]])
  const [Cypa2, setCypa2] = useAutoSaveState('Cypa2', [[0.00, 0.0], [0.95, 0.0], [1.00, 0.0], [1.05, 0.0], [1.10, 0.0], [1.20, 0.0], [1.40, 0.0], [1.60, 0.0], [1.80, 0.0], [2.00, 0.0], [2.20, 0.0], [2.50, 0.0]])
  const [Cnq, setCnq] = useAutoSaveState('Cnq', [[0.00, 0.0], [0.95, 0.0], [1.00, 0.0], [1.05, 0.0], [1.10, 0.0], [1.20, 0.0], [1.40, 0.0], [1.60, 0.0], [1.80, 0.0], [2.00, 0.0], [2.20, 0.0], [2.50, 0.0]])
  const [Cnq2, setCnq2] = useAutoSaveState('Cnq2', [[0.00, 0.0], [0.95, 0.0], [1.00, 0.0], [1.05, 0.0], [1.10, 0.0], [1.20, 0.0], [1.40, 0.0], [1.60, 0.0], [1.80, 0.0], [2.00, 0.0], [2.20, 0.0], [2.50, 0.0]])

  useEffect(() => {
    if (result) {
      drawAlphaBeta(result)
      drawPos(result)
    }
  }, [result])

  const calculate = () => {
    let simulation = new SixDofSim({
      mass: grainsToKg(parseFloat(mass)),
      Ix: gcm2ToKgm2(parseFloat(Ix)),
      Iy: gcm2ToKgm2(parseFloat(Iy)),
      airPressure: inhgToPa(parseFloat(airPressure)),
      diameter: inchesToMeters(parseFloat(diameter)),
      temperature: fToK(parseFloat(temperature)),
      windSpeed: mphToMps(parseFloat(windSpeed)),
      windDirection: degToRad(parseFloat(windDirection)),
      v0: fpsToMps(parseFloat(v0)),
      calibersPerTurn: parseFloat(twistRate) / parseFloat(diameter),
      phi0: parseFloat(phi0),
      theta0: parseFloat(theta0),
      pitchRate0: parseFloat(pitchRate0),
      yawRate0: parseFloat(yawRate0),
      alpha0: parseFloat(alpha0),
      beta0: parseFloat(beta0),
      Cd0,
      Cda2,
      Clp,
      Cla,
      Cma0,
      Cma2,
      CmqCma0,
      CmqCma2,
      Cmpa0,
      Cmpa2,
    })
    const start = Date.now()
    const newResult = simulation.integrate({ maxTime: parseFloat(maxTime), dt: parseFloat(dt), resultInterval: parseFloat(resultInterval) })
    const end = Date.now()
    console.log('Calculation time:', (end - start) / 1000, 's')
    setResult(newResult)
    window.gtag('event', 'calculate_6dof')
  }

  const drawAlphaBeta = result => {
    const canvas = canvasRef.current
    if (!canvas) return
    canvas.height = canvas.offsetHeight
    canvas.width = canvas.offsetWidth
    const ctx = canvas.getContext('2d')
    // Clear the canvas
    ctx.clearRect(0, 0, canvas.width, canvas.height)
    // scale to fit canvas
    // find scale to fit the canvas
    const maxAlpha = Math.max(...result.map(({ alpha }) => Math.abs(alpha)))
    const maxBeta = Math.max(...result.map(({ beta }) => Math.abs(beta)))
    const scale = Math.min(canvas.width / maxAlpha, canvas.height / maxBeta) * 0.5
    // draw the center
    ctx.fillStyle = 'rgba(0, 0, 0, 0.5)'
    ctx.beginPath()
    ctx.arc(canvas.width / 2, canvas.height / 2, 5, 0, Math.PI * 2)
    ctx.fill()
    // draw alpha vs beta
    result.forEach(({ alpha, beta }, i) => {
      if (i === 0) return // skip first point
      // make darker for each step
      const prev = result[i - 1] || { alpha: 0, beta: 0 }
      ctx.strokeStyle = `rgba(${255 * (i / result.length)}, 0, ${255 * (1 - i / result.length)}, 1)`
      ctx.beginPath()
      ctx.moveTo(canvas.width / 2 + prev.beta * scale, canvas.height / 2 - prev.alpha * scale)
      ctx.lineTo(canvas.width / 2 + beta * scale, canvas.height / 2 - alpha * scale)
      ctx.stroke()
    })
  }

  const drawPos = result => {
    const canvas = canvasRef2.current
    if (!canvas) return
    canvas.height = canvas.offsetHeight
    canvas.width = canvas.offsetWidth
    const ctx = canvas.getContext('2d')
    ctx.clearRect(0, 0, canvas.width, canvas.height)
    const maxY = Math.max(...result.map(({ posY }) => Math.abs(posY)))
    const maxZ = Math.max(...result.map(({ posZ }) => Math.abs(posZ)))
    const scale = Math.min(canvas.width / maxY, canvas.height / maxZ) * 0.5
    // draw the center
    ctx.fillStyle = 'rgba(0, 0, 0, 0.5)'
    ctx.beginPath()
    ctx.arc(canvas.width / 2, canvas.height / 2, 5, 0, Math.PI * 2)
    ctx.fill()
    // draw alpha vs beta
    result.forEach(({ posY, posZ }, i) => {
      if (i === 0) return // skip first point
      // make darker for each step
      const prev = result[i - 1] || { posY: 0, posZ: 0 }
      ctx.strokeStyle = `rgba(${255 * (i / result.length)}, 0, ${255 * (1 - i / result.length)}, 1)`
      ctx.beginPath()
      ctx.moveTo(canvas.width / 2 + prev.posZ * scale, canvas.height / 2 - prev.posY * scale)
      ctx.lineTo(canvas.width / 2 + posZ * scale, canvas.height / 2 - posY * scale)
      ctx.stroke()
    })
  }

  const formatTooltip = (value) => {
    return parseFloat(value).toFixed(3);
  };

  return (
    <Container maxWidth="lg" sx={{ mt: 4, mb: 4 }}>
      <Grid container spacing={3} justifyContent="center" >
        <Grid container justifyContent="center" xs={12} sx={{ position: 'relative' }}>
          <Typography variant="h2" component="h1" gutterBottom>
            6 Degrees of Freedom Calculator
          </Typography>
          <SaveButton />
        </Grid>
        <NameField />

        <Grid item xs={12}>
          <Paper sx={{ p: 2, display: 'flex', flexDirection: 'column' }}>
            <Box sx={{ borderBottom: 1, borderColor: 'divider', mb: 2 }}>
              <Tabs value={formTab} onChange={(e, v) => setFormTab(v)} variant="scrollable" scrollButtons="auto">
                <Tab label="Control" />
                <Tab label="Bullet Properties" />
                <Tab label="Initial Conditions" />
                <Tab label="Environment" />
                <Tab label="Coefficients" />
              </Tabs>
            </Box>

            {formTab === 0 && (
              <Grid container spacing={2}>
                <Grid item xs={12} sm={4}>
                  <TextField fullWidth label="Max Time" value={maxTime} onChange={e => setMaxTime(e.target.value)} InputProps={{ endAdornment: <InputAdornment position="end">s</InputAdornment> }} />
                </Grid>
                <Grid item xs={12} sm={4}>
                  <TextField fullWidth label="Time Step (dt)" value={dt} onChange={e => setDt(e.target.value)} InputProps={{ endAdornment: <InputAdornment position="end">s</InputAdornment> }} />
                </Grid>
                <Grid item xs={12} sm={4}>
                  <TextField fullWidth label="Result Interval" value={resultInterval} onChange={e => setResultInterval(e.target.value)} InputProps={{ endAdornment: <InputAdornment position="end">s</InputAdornment> }} />
                </Grid>
              </Grid>
            )}

            {formTab === 1 && (
              <Grid container spacing={2}>
                <Grid item xs={12} sm={4}>
                  <TextField fullWidth label="Mass" value={mass} onChange={e => setMass(e.target.value)} InputProps={{ endAdornment: <InputAdornment position="end">grains</InputAdornment> }} />
                </Grid>
                <Grid item xs={12} sm={4}>
                  <TextField fullWidth label="Ix" value={Ix} onChange={e => setIx(e.target.value)} InputProps={{ endAdornment: <InputAdornment position="end">g.cm²</InputAdornment> }} />
                </Grid>
                <Grid item xs={12} sm={4}>
                  <TextField fullWidth label="Iy" value={Iy} onChange={e => setIy(e.target.value)} InputProps={{ endAdornment: <InputAdornment position="end">g.cm²</InputAdornment> }} />
                </Grid>
                <Grid item xs={12} sm={4}>
                  <TextField fullWidth label="Diameter" value={diameter} onChange={e => setDiameter(e.target.value)} InputProps={{ endAdornment: <InputAdornment position="end">in</InputAdornment> }} />
                </Grid>
                <Grid item xs={12} sm={4}>
                  <TextField fullWidth label="Twist Rate" value={twistRate} onChange={e => setTwistRate(e.target.value)} InputProps={{ endAdornment: <InputAdornment position="end">in/rev</InputAdornment> }} />
                </Grid>
              </Grid>
            )}

            {formTab === 2 && (
              <Grid container spacing={2}>
                <Grid item xs={12} sm={4}>
                  <TextField fullWidth label="Initial Velocity" value={v0} onChange={e => setV0(e.target.value)} InputProps={{ endAdornment: <InputAdornment position="end">fps</InputAdornment> }} />
                </Grid>
                <Grid item xs={12} sm={4}>
                  <TextField fullWidth label="Vertical Angle" value={phi0} onChange={e => setPhi0(e.target.value)} InputProps={{ endAdornment: <InputAdornment position="end">rad</InputAdornment> }} />
                </Grid>
                <Grid item xs={12} sm={4}>
                  <TextField fullWidth label="Horizontal Angle" value={theta0} onChange={e => setTheta0(e.target.value)} InputProps={{ endAdornment: <InputAdornment position="end">rad</InputAdornment> }} />
                </Grid>
                <Grid item xs={12} sm={4}>
                  <TextField fullWidth label="Pitch Rate" value={pitchRate0} onChange={e => setPitchRate0(e.target.value)} InputProps={{ endAdornment: <InputAdornment position="end">rad/s</InputAdornment> }} />
                </Grid>
                <Grid item xs={12} sm={4}>
                  <TextField fullWidth label="Yaw Rate" value={yawRate0} onChange={e => setYawRate0(e.target.value)} InputProps={{ endAdornment: <InputAdornment position="end">rad/s</InputAdornment> }} />
                </Grid>
                <Grid item xs={12} sm={4}>
                  <TextField fullWidth label="Alpha" value={alpha0} onChange={e => setAlpha0(e.target.value)} InputProps={{ endAdornment: <InputAdornment position="end">rad</InputAdornment> }} />
                </Grid>
                <Grid item xs={12} sm={4}>
                  <TextField fullWidth label="Beta" value={beta0} onChange={e => setBeta0(e.target.value)} InputProps={{ endAdornment: <InputAdornment position="end">rad</InputAdornment> }} />
                </Grid>
              </Grid>
            )}

            {formTab === 3 && (
              <Grid container spacing={2}>
                <Grid item xs={12} sm={4}>
                  <TextField fullWidth label="Air Pressure" value={airPressure} onChange={e => setAirPressure(e.target.value)} InputProps={{ endAdornment: <InputAdornment position="end">inHg</InputAdornment> }} />
                </Grid>
                <Grid item xs={12} sm={4}>
                  <TextField fullWidth label="Temperature" value={temperature} onChange={e => setTemperature(e.target.value)} InputProps={{ endAdornment: <InputAdornment position="end">°F</InputAdornment> }} />
                </Grid>
                <Grid item xs={12} sm={4}>
                  <TextField fullWidth label="Wind Speed" value={windSpeed} onChange={e => setWindSpeed(e.target.value)} InputProps={{ endAdornment: <InputAdornment position="end">mph</InputAdornment> }} />
                </Grid>
                <Grid item xs={12} sm={4}>
                  <TextField fullWidth label="Wind Direction" value={windDirection} onChange={e => setWindDirection(e.target.value)} InputProps={{ endAdornment: <InputAdornment position="end">deg</InputAdornment> }} />
                </Grid>
              </Grid>
            )}

            {formTab === 4 && (
              <Grid container spacing={2}>
                <Box sx={{ borderBottom: 1, borderColor: 'divider', mb: 2, width: '100%' }}>
                  <Tabs value={coeffTab} onChange={(e, v) => setCoeffTab(v)} variant="scrollable" scrollButtons="auto">
                    <Tab label="Drag" />
                    <Tab label="Lift" />
                    <Tab label="Pitch" />
                    <Tab label="Roll" />
                    <Tab label="Magnus" />
                    <Tab label="Damping" />
                    <Tab label="Magnus Force" />
                    <Tab label="Damp Force" />
                  </Tabs>
                </Box>
                {coeffTab === 0 && (
                  <>
                    <Grid item xs={12} md={6}>
                      <CoefficientTable name="Cd0" data={Cd0} onChange={setCd0} />
                    </Grid>
                    <Grid item xs={12} md={6}>
                      <CoefficientTable name="Cda2" data={Cda2} onChange={setCda2} />
                    </Grid>
                  </>
                )}
                {coeffTab === 1 && (
                  <>
                    <Grid item xs={12} md={6}>
                      <CoefficientTable name="Cla" data={Cla} onChange={setCla} />
                    </Grid>
                    <Grid item xs={12} md={6}>
                      <CoefficientTable name="Cla2" data={Cla2} onChange={setCla2} />
                    </Grid>
                  </>
                )}
                {coeffTab === 2 && (
                  <>
                    <Grid item xs={12} md={6}>
                      <CoefficientTable name="Cma0" data={Cma0} onChange={setCma0} />
                    </Grid>
                    <Grid item xs={12} md={6}>
                      <CoefficientTable name="Cma2" data={Cma2} onChange={setCma2} />
                    </Grid>
                  </>
                )}
                {coeffTab === 3 && (
                  <>
                    <Grid item xs={12} md={6}>
                      <CoefficientTable name="Clp" data={Clp} onChange={setClp} />
                    </Grid>
                  </>
                )}
                {coeffTab === 4 && (
                  <>
                    <Grid item xs={12} md={6}>
                      <CoefficientTable name="Cmpa0" data={Cmpa0} onChange={setCmpa0} />
                    </Grid>
                    <Grid item xs={12} md={6}>
                      <CoefficientTable name="Cmpa2" data={Cmpa2} onChange={setCmpa2} />
                    </Grid>
                  </>
                )}
                {coeffTab === 5 && (
                  <>
                    <Grid item xs={12} md={6}>
                      <CoefficientTable name="CmqCma0" data={CmqCma0} onChange={setCmqCma0} />
                    </Grid>
                    <Grid item xs={12} md={6}>
                      <CoefficientTable name="CmqCma2" data={CmqCma2} onChange={setCmqCma2} />
                    </Grid>
                  </>
                )}
                {coeffTab === 6 && (
                  <>
                    <Grid item xs={12} md={6}>
                      <CoefficientTable name="Cypa" data={Cypa} onChange={setCypa} />
                    </Grid>
                    <Grid item xs={12} md={6}>
                      <CoefficientTable name="Cypa2" data={Cypa2} onChange={setCypa2} />
                    </Grid>
                  </>
                )}
                {coeffTab === 7 && (
                  <>
                    <Grid item xs={12} md={6}>
                      <CoefficientTable name="Cnq" data={Cnq} onChange={setCnq} />
                    </Grid>
                    <Grid item xs={12} md={6}>
                      <CoefficientTable name="Cnq2" data={Cnq2} onChange={setCnq2} />
                    </Grid>
                  </>
                )}
              </Grid>
            )}
          </Paper>
        </Grid>

        {/* Calculate Button */}
        <Grid item xs={12}>
          <Paper sx={{ p: 2, display: 'flex', flexDirection: 'column' }}>
            <Grid container xs={12} justifyContent="center">
              <Button variant="contained" onClick={calculate}>Calculate</Button>
            </Grid>
          </Paper>
        </Grid>

        {/* Results Section */}
        {result && (
          <Grid item xs={12}>
            <Paper sx={{ p: 2, display: 'flex', flexDirection: 'column' }}>
              <Grid container xs={12} justifyContent="center">
                <Typography variant="h5" gutterBottom>
                  <EqualizerIcon /> Results
                </Typography>
              </Grid>
              <Divider style={{ marginBottom: '20px', marginTop: '20px' }} />
              <Grid container spacing={2}>
                <Grid item xs={10}>
                  <Box display="flex" justifyContent="space-around" mb={2}>
                    <canvas ref={canvasRef} style={{ border: '1px solid #aaa', width: '100%', aspectRatio: '1 / 1' }}/>
                  </Box>
                </Grid>
                <Grid item xs={10}>
                  <Box display="flex" justifyContent="space-around" mb={2}>
                    <canvas ref={canvasRef2} style={{ border: '1px solid #aaa', width: '100%', aspectRatio: '1 / 1' }}/>
                  </Box>
                </Grid>
                <Grid item xs={12}>
                  <Box sx={{ height: 300, mb: 4 }}>
                    <Typography variant="h6" gutterBottom>Alpha and Beta Angles</Typography>
                    <ResponsiveContainer width="100%" height="100%">
                      <LineChart data={result}>
                        <XAxis dataKey="time" />
                        <YAxis />
                        <Tooltip formatter={formatTooltip} />
                        <Legend />
                        <Line type="monotone" dataKey="alphaDeg" stroke="#8884d8" name="Alpha (deg)" dot={false} />
                        <Line type="monotone" dataKey="betaDeg" stroke="#82ca9d" name="Beta (deg)" dot={false} />
                        <Line type="monotone" dataKey="alphaTDeg" stroke="#f2aa7d" name="AoA (deg)" dot={false} />
                      </LineChart>
                    </ResponsiveContainer>
                  </Box>
                </Grid>
                <Grid item xs={12}>
                  <Box sx={{ height: 300, mb: 4 }}>
                    <Typography variant="h6" gutterBottom>Velocity Components</Typography>
                    <ResponsiveContainer width="100%" height="100%">
                      <LineChart data={result.map(point => ({
                        ...point,
                        v: mpsToFps(point.v),
                        vx: mpsToFps(point.vx),
                        vy: mpsToFps(point.vy),
                        vz: mpsToFps(point.vz)
                      }))}>
                        <XAxis dataKey="time" />
                        <YAxis />
                        <Tooltip formatter={formatTooltip} />
                        <Legend />
                        <Line type="monotone" dataKey="v" stroke="#8884d8" name="Total Velocity (fps)" dot={false} />
                        <Line type="monotone" dataKey="vx" stroke="#82ca9d" name="Vx (fps)" dot={false} />
                        <Line type="monotone" dataKey="vy" stroke="#ffc658" name="Vy (fps)" dot={false} />
                        <Line type="monotone" dataKey="vz" stroke="#ff7300" name="Vz (fps)" dot={false} />
                      </LineChart>
                    </ResponsiveContainer>
                  </Box>
                </Grid>
                <Grid item xs={12}>
                  <Box sx={{ height: 300, mb: 4 }}>
                    <Typography variant="h6" gutterBottom>Position Components</Typography>
                    <ResponsiveContainer width="100%" height="100%">
                      <LineChart data={result.map(point => ({
                        ...point,
                        posX: metersToYards(point.posX),
                        posY: metersToInches(point.posY),
                        posZ: metersToInches(point.posZ)
                      }))}>
                        <XAxis dataKey="time" />
                        <YAxis />
                        <Tooltip formatter={formatTooltip} />
                        <Legend />
                        <Line type="monotone" dataKey="posX" stroke="#8884d8" name="X Position (yd)" dot={false} />
                        <Line type="monotone" dataKey="posY" stroke="#82ca9d" name="Y Position (in)" dot={false} />
                        <Line type="monotone" dataKey="posZ" stroke="#ffc658" name="Z Position (in)" dot={false} />
                      </LineChart>
                    </ResponsiveContainer>
                  </Box>
                </Grid>
                <Grid item xs={12}>
                  <Box sx={{ height: 300, mb: 4 }}>
                    <Typography variant="h6" gutterBottom>Angular Rates</Typography>
                    <ResponsiveContainer width="100%" height="100%">
                      <LineChart data={result}>
                        <XAxis dataKey="time" />
                        <YAxis />
                        <Tooltip formatter={formatTooltip} />
                        <Legend />
                        <Line type="monotone" dataKey="rollRate" stroke="#42caad" name="Roll Rate (rad/s)" dot={false} />
                        <Line type="monotone" dataKey="pitchRate" stroke="#82ca9d" name="Pitch Rate (rad/s)" dot={false} />
                        <Line type="monotone" dataKey="yawRate" stroke="#ffc658" name="Yaw Rate (rad/s)" dot={false} />
                      </LineChart>
                    </ResponsiveContainer>
                  </Box>
                </Grid>
                <Grid item xs={12}>
                  <Box sx={{ height: 300, mb: 4 }}>
                    <Typography variant="h6" gutterBottom>Spin Rate</Typography>
                    <ResponsiveContainer width="100%" height="100%">
                      <LineChart data={result}>
                        <XAxis dataKey="time" />
                        <YAxis />
                        <Tooltip formatter={formatTooltip} />
                        <Legend />
                        <Line type="monotone" dataKey="spinRate" stroke="#82ca9d" name="Spin Rate (rad/s)" dot={false} />
                      </LineChart>
                    </ResponsiveContainer>
                  </Box>
                </Grid>
                <Grid item xs={12}>
                  <Box sx={{ height: 300, mb: 4 }}>
                    <Typography variant="h6" gutterBottom>Stability</Typography>
                    <ResponsiveContainer width="100%" height="100%">
                      <LineChart data={result.map(point => ({
                        ...point,
                        Sd: Math.min(Math.max(point.Sd, -10), 10),
                        Sg: Math.min(Math.max(point.Sg, -10), 10),
                      }))}>
                        <XAxis dataKey="time" />
                        <YAxis />
                        <Tooltip formatter={formatTooltip} />
                        <Legend />
                        <Line type="monotone" dataKey="Sg" stroke="#82ca9d" name="Sg" dot={false} />
                        <Line type="monotone" dataKey="Sd" stroke="#f42a32" name="Sd" dot={false} />
                        <Line type="monotone" dataKey="stable" stroke="#c2418a" name="stable" dot={false} />
                      </LineChart>
                    </ResponsiveContainer>
                  </Box>
                </Grid>
                <Grid item xs={12}>
                  <Box sx={{ height: 300, mb: 4 }}>
                    <Typography variant="h6" gutterBottom>Coeffs</Typography>
                    <ResponsiveContainer width="100%" height="100%">
                      <LineChart data={result}>
                        <XAxis dataKey="time" />
                        <YAxis />
                        <Tooltip formatter={formatTooltip} />
                        <Legend />
                        <Line type="monotone" dataKey="cd" stroke="#8884d8" name="Cd" dot={false} />
                        <Line type="monotone" dataKey="clα" stroke="#82ca9d" name="Clα" dot={false} />
                        <Line type="monotone" dataKey="cmα" stroke="#ffc658" name="Cmα" dot={false} />
                        <Line type="monotone" dataKey="cmq" stroke="#177340" name="Cmq" dot={false} />
                        <Line type="monotone" dataKey="clp" stroke="#2f73f0" name="Clp" dot={false} />
                        <Line type="monotone" dataKey="cmpα" stroke="#ff7300" name="Cmpα" dot={false} />
                      </LineChart>
                    </ResponsiveContainer>
                  </Box>
                </Grid>
              </Grid>
            </Paper>
          </Grid>
        )}
      </Grid>
    </Container>
  )
}

export default function WrappedTemplate () {
  return <AutoSaveProvider>
    <SixDof />
  </AutoSaveProvider>
}
